import React, { type JSX } from "react";

import ResizableDividerHorizontal from "../../ResizableDividerHorizontal_Component";
import { type DocumentFacts } from "./useDocumentFacts";
import { type FactData } from "./documentPreviewTypes";

/** Snippets are trimmed to keep every row roughly the same height. */
const MAX_SNIPPET_LENGTH = 280;
const TRIMMED_SNIPPET_LENGTH = 277;

/** Both image and text facts carry their evidence in highlightContentString. */
function snippetOf(fact: FactData): string {
  const rawSnippet =
    fact.highlightContentString ||
    fact.surroundingContentString ||
    fact.factFromImage || // Legacy fallback for older image facts
    "";
  return rawSnippet.length > MAX_SNIPPET_LENGTH
    ? `${rawSnippet.slice(0, TRIMMED_SNIPPET_LENGTH)}…`
    : rawSnippet;
}

function FactPages({ fact }: { fact: FactData }): JSX.Element | null {
  // Prefer pageIndices (multi-page) over the single pageIndex
  if (fact.pageIndices && fact.pageIndices.length > 0) {
    return (
      <p className="text-xs text-gray-400 mt-2">
        {fact.pageIndices.length === 1
          ? `Page ${fact.pageIndices[0] + 1}`
          : `Pages ${fact.pageIndices.map((p) => p + 1).join(", ")}`}
      </p>
    );
  }
  if (fact.pageIndex === undefined || fact.pageIndex === null) {
    return null;
  }
  return <p className="text-xs text-gray-400 mt-2">Page {fact.pageIndex + 1}</p>;
}

interface FactRowProps {
  fact: FactData;
  displayNumber: number;
  isActive: boolean;
  onSelect: () => void;
  registerRow: (el: HTMLDivElement | null) => void;
}

function FactRow({
  fact,
  displayNumber,
  isActive,
  onSelect,
  registerRow,
}: FactRowProps): JSX.Element {
  const isImageFact = fact.factSourceType === "image";
  const snippet = snippetOf(fact);

  return (
    <div
      ref={registerRow}
      onClick={onSelect}
      className={`p-3 rounded border cursor-pointer transition-all hover:bg-cvc-lb2 ${
        isActive
          ? "bg-cvc-lb1 border-cvc-blue ring-2 ring-cvc-blue ring-offset-1"
          : "bg-gray-50 border-cvc-grey hover:border-cvc-lb1"
      }`}
    >
      <div className="flex items-start">
        <span
          className={`inline-flex items-center justify-center w-6 h-6 mr-3 text-xs font-semibold rounded-full shrink-0 ${
            isActive ? "text-white bg-cvc-blue" : "text-cvc-text bg-gray-200"
          }`}
        >
          {displayNumber}
        </span>
        <div className="flex-1">
          <div className="flex items-start gap-2 mb-1">
            <p className="text-sm text-gray-700 font-medium flex-1">
              {fact.rationale || "No rationale provided"}
            </p>
            <span
              className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                isImageFact
                  ? "bg-blue-100 text-blue-700 border border-blue-200"
                  : "bg-green-100 text-green-700 border border-green-200"
              }`}
            >
              {isImageFact ? "Image analysis" : "Text analysis"}
            </span>
          </div>
          {snippet && <p className="text-xs text-gray-500 italic">"{snippet}"</p>}
          <FactPages fact={fact} />
        </div>
      </div>
    </div>
  );
}

/** The resizable list of facts cited from the document being previewed. */
export default function FactsPanel({ facts }: { facts: DocumentFacts }): JSX.Element {
  const { sortedDocumentFacts, clickedFact, factIdToIndex, factRowRefs } = facts;

  return (
    <>
      <ResizableDividerHorizontal onDragMove={facts.onDividerDragMove} />

      <div
        className="border-t border-gray-200 bg-white flex flex-col shrink-0"
        style={{ height: `${facts.factsPanelHeight}px` }}
      >
        {/* Header - always visible at top */}
        <div className="shrink-0 px-4 pt-4 pb-2">
          <h4 className="text-sm font-cvcDisplay font-semibold text-cvc-blue">
            Facts from this document ({sortedDocumentFacts.length}):
          </h4>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-4">
          <div className="space-y-2">
            {sortedDocumentFacts.map((fact, index) => (
              <FactRow
                key={fact.factId || index}
                fact={fact}
                // Use chat numbering if available, otherwise fall back to array index
                displayNumber={
                  fact.factId ? (factIdToIndex?.[fact.factId] ?? index + 1) : index + 1
                }
                isActive={Boolean(clickedFact && fact.factId === clickedFact.factId)}
                onSelect={() => facts.onFactClick(fact, index)}
                registerRow={(el) => {
                  if (fact.factId && el) {
                    factRowRefs.current[fact.factId] = el;
                  }
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
